import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import {
  AppBar,
  Box,
  Button,
  Card,
  Dialog,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { FORM_URL } from "../../config/config";

interface FormStatusSectionProps {
  submission: any;
  onCloseForm?: () => void;
  authToken: string;
}

export const FormStatusSection: React.FC<FormStatusSectionProps> = ({
  submission,
  onCloseForm,
  authToken,
}) => {
  const [open, setOpen] = useState(false);

  const getStatus = () => {
    if (!submission) return "NOT_STARTED";
    if (submission.json && !submission.finishedAt) return "IN_PROGRESS";
    if (submission.json && submission.finishedAt) return "COMPLETED";
    return "NOT_STARTED";
  };

  const status = getStatus();

  const statusConfig = {
    NOT_STARTED: {
      label: "Aún no has iniciado tu formulario",
      color: "error.main",
      icon: <HourglassEmptyRoundedIcon sx={{ color: "error.main" }} />,
    },
    IN_PROGRESS: {
      label: "Formulario en progreso",
      color: "warning.main",
      icon: <DescriptionRoundedIcon sx={{ color: "warning.main" }} />,
    },
    COMPLETED: {
      label: "Formulario completado 🎉",
      color: "success.main",
      icon: <CheckCircleRoundedIcon sx={{ color: "success.main" }} />,
    },
  }[status];

  const handleClose = () => {
    setOpen(false);
    if (onCloseForm) onCloseForm();
  };

  return (
    <>
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          p: 2,
          mb: 3,
          background: "linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)",
          border: "1px solid rgba(0,0,0,0.05)",
          boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              bgcolor: "rgba(0,0,0,0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {statusConfig.icon}
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color="text.primary"
            >
              Estado del formulario
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: statusConfig.color, fontWeight: 600 }}
            >
              {statusConfig.label}
            </Typography>
          </Box>
        </Box>

        {status !== "COMPLETED" && (
          <Button
            variant="contained"
            color="secondary"
            disableElevation
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 160,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {"Abrir formulario"}
          </Button>
        )}
      </Card>

      <Dialog fullScreen open={open} onClose={handleClose}>
        <AppBar
          elevation={0}
          sx={{
            position: "relative",
            background:
              "linear-gradient(90deg, #0B1E6E 0%, #123BFF 60%, #0066FF 100%)",
            color: "white",
            boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              minHeight: 56,
              px: { xs: 1.5, sm: 2.5 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box>
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  sx={{
                    letterSpacing: 0.2,
                    lineHeight: 1.2,
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  Formulario DS-160
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    letterSpacing: 0.2,
                    display: "block",
                    fontSize: "0.75rem",
                  }}
                >
                  Registro de solicitud de visa
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleClose}
              aria-label="Cerrar formulario"
              sx={{
                color: "#fff",
                ml: 1,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, bgcolor: "#f5f5f5", height: "100%", p: 0 }}>
          {FORM_URL ? (
            <iframe
              title="Formulario DS-160 embebido"
              src={`${FORM_URL}?token=${authToken}`}
              style={{
                border: "none",
                width: "100%",
                height: "100%",
              }}
              allowFullScreen
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                flexDirection: "column",
                color: "text.secondary",
              }}
            >
              <DescriptionRoundedIcon
                sx={{ fontSize: 60, mb: 2, opacity: 0.4 }}
              />
              <Typography variant="h6">
                No hay un formulario asignado todavía
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </>
  );
};
